import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { AlertCircle, ArrowDown, ArrowUp, CheckSquare, ChevronDown, ChevronRight, Plus, RefreshCw, Search, Sparkles, X } from 'lucide-react'
import { useProvideFeedbackContext } from '../components/feedback/FeedbackContext'
import { Button } from '../components/ui/Button'
import { useToast } from '../components/ui/Toast'
import { ConfirmationModal } from '../components/ui/ConfirmationModal'
import { SkeletonList } from '../components/ui/Skeleton'
import { TaskService, type Task, type CreateTaskRequest } from '../services/taskService'
import { ActionItemService, type ActionItem } from '../services/actionItemService'
import { useTheme } from '../components/providers/ThemeProvider'
import { useBranding } from '../hooks/useBranding'
import { describeTaskCleanup, lifeAreaLabel } from '../utils/taskForm'
import { actionItemIdOf, groupTasks, matchesQuery, summarize, summaryWords, type GroupBy, type SortBy, type SortDir } from '../utils/taskSections'
import { TaskRow } from '../components/tasks/TaskRow'
import { TaskQuickAdd } from '../components/tasks/TaskQuickAdd'

/**
 * The task list, redesigned 2026-09-05 around how a person reads a list of
 * things to do:
 *
 *   - One title, one summary line, two verbs — the same frame as a task page.
 *   - Capture first: a quick-add row at the top; a title is enough.
 *   - Sections by attention (Doing now · Needs a look · This week · Later ·
 *     Done) instead of one flat, paginated, colour-coded list; Goal and Life
 *     area groupings are one click away and remembered.
 *   - A row is a mark, a title and one quiet line. Tap the mark to finish
 *     (with Undo); Plan it stays in view for anything without a time; the
 *     rest is under ⋯.
 *   - Search instead of pagination. Done is collapsed, newest first.
 *   - Tone: "Needs a look", "Past its time", "Let go" — never overdue/failed.
 */
const hasParentId = (t: Task) => Boolean(t.parent_goal_id || t.parent_skill_id || t.parent_aspiration_id || t.parent_dream_id)
const GROUP_PREF = 'tasks.groupBy'
const SORT_PREF = 'tasks.sortBy'
const DIR_PREF = 'tasks.sortDir'
const DONE_PREVIEW = 8

function readPref<T extends string>(key: string, allowed: readonly T[], fallback: T): T {
  try {
    const v = localStorage.getItem(key)
    return (allowed as readonly string[]).includes(v || '') ? (v as T) : fallback
  } catch { return fallback }
}
function writePref(key: string, value: string) {
  try { localStorage.setItem(key, value) } catch { /* per-viewer convenience only */ }
}

export const Tasks: React.FC = () => {
  const { isDiary: diaryMode } = useTheme()
  const { assistant: personaName } = useBranding()
  const navigate = useNavigate()
  const toast = useToast()
  const [tasks, setTasks] = useState<Task[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [parentIssue, setParentIssue] = useState<string | null>(null)
  const [query, setQuery] = useState('')
  const [groupBy, setGroupByState] = useState<GroupBy>(() => readPref(GROUP_PREF, ['when', 'goal', 'area', 'none'] as const, 'when'))
  const [sortBy, setSortByState] = useState<SortBy>(() => readPref(SORT_PREF, ['attention', 'created', 'updated'] as const, 'attention'))
  const [sortDir, setSortDirState] = useState<SortDir>(() => readPref(DIR_PREF, ['asc', 'desc'] as const, 'desc'))
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({})
  const [showAllDone, setShowAllDone] = useState(false)
  const [deleteTarget, setDeleteTarget] = useState<Task | null>(null)
  const [loadedAt, setLoadedAt] = useState(() => new Date())
  const taskService = useMemo(() => new TaskService(), [])

  useProvideFeedbackContext({ pageType: 'tasks_list', extra: { groupBy, sortBy, sortDir, searching: Boolean(query) } })

  const setGroupBy = (g: GroupBy) => { setGroupByState(g); writePref(GROUP_PREF, g) }
  const setSortBy = (s: SortBy) => { setSortByState(s); writePref(SORT_PREF, s) }
  const setSortDir = (d: SortDir) => { setSortDirState(d); writePref(DIR_PREF, d) }

  // ONE list of things to do (owner-ratified IA): action items render here as
  // rows beside tasks with synthetic negative ids; their own pages stay canonical.
  const load = useCallback(async () => {
    try {
      setError(null)
      const [tasksData, aiData] = await Promise.all([
        taskService.getTasks(),
        new ActionItemService().getActionItems().catch(() => [] as ActionItem[]),
      ])
      const aiRows: Task[] = (Array.isArray(aiData) ? aiData : []).map((ai, i) => ({
        id: -(i + 1), title: ai.title, description: ai.description, status: ai.status, priority: ai.priority,
        progress: ai.progress, due_date: ai.due_date, created_at: ai.created_at, updated_at: ai.updated_at,
        __actionItemId: ai.id,
        __parentTaskId: ai.parent_task_id,
      }) as unknown as Task)
      let rows: Task[] = Array.isArray(tasksData) ? tasksData : []
      // Legacy rows whose parent the server could not name: fill the title
      // from the goal/skill lists so grouping by goal still works.
      if (rows.some((t) => hasParentId(t) && !t.parent?.title)) {
        try {
          const [g, s] = await Promise.all([
            fetch('/api/hierarchy/goals', { credentials: 'include' }).then((r) => r.json()),
            fetch('/api/hierarchy/skills', { credentials: 'include' }).then((r) => r.json()),
          ])
          const titleOf = new Map<string, string>()
          for (const x of (Array.isArray(g) ? g : g.data || [])) titleOf.set(`goal:${x.id}`, x.title)
          for (const x of (Array.isArray(s) ? s : s.data || [])) titleOf.set(`skill:${x.id}`, x.title)
          rows = rows.map((t) => {
            if (t.parent?.title) return t
            const type = t.parent_goal_id ? 'goal' : t.parent_skill_id ? 'skill' : null
            const id = t.parent_goal_id || t.parent_skill_id
            const title = type && id ? titleOf.get(`${type}:${id}`) : undefined
            return title && type && id ? { ...t, parent: { type, id, title, life_area: t.parent?.life_area ?? null } } : t
          })
          setParentIssue(null)
        } catch {
          setParentIssue('We could not load what some of these sit under, so they are shown on their own.')
        }
      }
      setTasks([...rows, ...aiRows])
      setLoadedAt(new Date())
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load tasks')
    } finally {
      setLoading(false)
    }
  }, [taskService])

  useEffect(() => { void load() }, [load])

  const visible = useMemo(() => tasks.filter((t) => matchesQuery(t, query)), [tasks, query])
  const groups = useMemo(() => groupTasks(visible, groupBy, loadedAt, lifeAreaLabel, { by: sortBy, dir: sortDir }), [visible, groupBy, loadedAt, sortBy, sortDir])
  const summary = useMemo(() => summarize(tasks, loadedAt), [tasks, loadedAt])

  // ── verbs ──
  const reopen = async (task: Task) => {
    const aid = actionItemIdOf(task)
    try {
      if (aid) await new ActionItemService().updateActionItem(aid, { status: 'pending' })
      else await taskService.updateTask(task.id, { status: 'pending' })
      await load()
    } catch (err) {
      toast.error('Could not reopen it', err instanceof Error ? err.message : 'Please try again.')
    }
  }

  const complete = async (task: Task) => {
    const aid = actionItemIdOf(task)
    try {
      let unlocked: { id: number; title: string; estimated_duration?: number } | undefined
      if (aid) await new ActionItemService().updateActionItem(aid, { status: 'completed' })
      else unlocked = (await taskService.markTaskComplete(task.id)).journey_step_unlocked
      await load()
      if (unlocked) {
        const u = unlocked
        toast.toast({ variant: 'info', title: 'Next step unlocked', description: u.title, action: { label: 'Schedule now', onClick: () => navigate('/review-schedule', { state: { items: [{ id: u.id, type: 'task' as const, title: u.title, priority: 'medium', estimated_duration: u.estimated_duration || 30 }] } }) } })
      } else {
        // Forgiveness over confirmation: one tap finishes it, Undo is right there.
        toast.toast({ variant: 'success', title: 'Done', description: task.title, action: { label: 'Undo', onClick: () => void reopen(task) } })
      }
    } catch (err) {
      toast.error(aid ? 'Could not finish the step' : 'Could not finish the task', err instanceof Error ? err.message : 'Please try again.')
    }
  }

  const confirmDelete = async () => {
    if (!deleteTarget) return
    const target = deleteTarget
    try {
      const aid = actionItemIdOf(target)
      let cleanupNote: string | null = null
      if (aid) await new ActionItemService().deleteActionItem(aid)
      else cleanupNote = describeTaskCleanup((await taskService.deleteTask(target.id)).cleanup)
      setDeleteTarget(null)
      await load()
      toast.toast({ variant: 'success', title: aid ? 'Step deleted' : 'Task deleted', description: cleanupNote || undefined })
    } catch (err) {
      setDeleteTarget(null)
      toast.error('Could not delete it', err instanceof Error ? err.message : 'Please try again.')
    }
  }

  const quickAdd = async (title: string) => {
    const created = await taskService.createTask({ title, priority: 'medium' } as CreateTaskRequest)
    await load()
    const id = Number(created?.id)
    toast.toast({
      variant: 'success', title: 'Added', description: title,
      action: Number.isFinite(id) ? { label: 'Open', onClick: () => navigate(`/tasks/${id}`) } : undefined,
    })
  }

  const toggle = (key: string, def: boolean) => setCollapsed((c) => ({ ...c, [key]: !(c[key] ?? def) }))

  // ── frame ──
  if (loading) {
    return <div className="space-y-4" aria-busy="true" aria-label="Loading tasks"><SkeletonList count={5} /></div>
  }
  if (error && tasks.length === 0) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="text-center" role="alert">
          <AlertCircle className="mx-auto mb-4 h-12 w-12 text-bad" />
          <h2 className="mb-2 text-xl font-semibold text-primary">The list would not load</h2>
          <p className="mb-4 text-secondary">{error}</p>
          <Button onClick={() => { setLoading(true); void load() }}><RefreshCw className="mr-2 h-4 w-4" />Try again</Button>
        </div>
      </div>
    )
  }

  const empty = tasks.length === 0
  const nothingMatches = !empty && visible.length === 0

  return (
    <div className="space-y-4" data-testid="tasks-page">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex min-w-0 items-center gap-3">
          <span className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-brand sm:h-10 sm:w-10" aria-hidden="true" data-testid="tasks-logo">
            <CheckSquare className="h-5 w-5 text-on-brand" />
          </span>
          <div className="min-w-0">
            <h1 className={`text-xl font-semibold leading-tight text-primary sm:text-2xl ${diaryMode ? 'font-handwritten' : ''}`}>Tasks</h1>
            <p className="text-app-meta text-secondary" data-testid="tasks-summary">{summaryWords(summary)}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button size="xs" onClick={() => navigate('/tasks/create')} data-testid="tasks-new">
            <Plus className="mr-1 h-4 w-4" />New task
          </Button>
          <Button size="xs" variant="outline" onClick={() => navigate('/horton-ai?from=tasks&type=all')} data-testid="tasks-ask">
            <Sparkles className="mr-1 h-4 w-4" />{personaName}
          </Button>
        </div>
      </div>

      {parentIssue && (
        <div className="flex flex-wrap items-center gap-2 rounded-lg border border-tint-bad-edge bg-tint-bad-soft p-3 text-bad" role="alert">
          <AlertCircle className="h-4 w-4 shrink-0" />
          <span className="text-app-meta">{parentIssue}</span>
          <Button onClick={() => void load()} variant="outline" size="xs" className="ml-auto shrink-0"><RefreshCw className="mr-1.5 h-4 w-4" />Try again</Button>
        </div>
      )}

      {!empty && (
        <div className="flex flex-wrap items-center justify-between gap-2">
          <label className="relative block w-full sm:w-64 lg:w-80">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted" aria-hidden="true" />
            <input
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Find a task"
              aria-label="Find a task"
              className="min-h-touch w-full rounded-md border border-soft bg-surface pl-8 pr-8 text-sm text-primary placeholder:text-muted focus:outline-none focus:ring-2 focus:ring-brand/30 lg:min-h-0 lg:py-1.5"
              data-testid="tasks-search"
            />
            {query && (
              <button type="button" onClick={() => setQuery('')} aria-label="Clear search" className="absolute right-1.5 top-1/2 -translate-y-1/2 rounded p-1 text-muted hover:text-primary">
                <X className="h-3.5 w-3.5" />
              </button>
            )}
          </label>
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 text-app-micro">
            <div className="flex items-center gap-1.5">
              <span className="text-muted">Group by</span>
              <div role="radiogroup" aria-label="Group by" className="inline-flex rounded-md border border-soft bg-surface p-0.5">
                {([['when', 'When'], ['goal', 'Goal'], ['area', 'Life area'], ['none', 'None']] as Array<[GroupBy, string]>).map(([k, label]) => (
                  <button
                    key={k}
                    type="button"
                    role="radio"
                    aria-checked={groupBy === k}
                    onClick={() => setGroupBy(k)}
                    className={`rounded px-2 py-1 font-medium transition-colors ${groupBy === k ? 'bg-brand-subtle text-brand' : 'text-secondary hover:text-primary'}`}
                    data-testid={`tasks-group-${k}`}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>
            <div className="flex items-center gap-1.5">
              <span className="text-muted">Sort</span>
              <div role="radiogroup" aria-label="Sort" className="inline-flex rounded-md border border-soft bg-surface p-0.5">
                {([['attention', 'Attention'], ['created', 'Created'], ['updated', 'Updated']] as Array<[SortBy, string]>).map(([k, label]) => (
                  <button
                    key={k}
                    type="button"
                    role="radio"
                    aria-checked={sortBy === k}
                    onClick={() => setSortBy(k)}
                    className={`rounded px-2 py-1 font-medium transition-colors ${sortBy === k ? 'bg-brand-subtle text-brand' : 'text-secondary hover:text-primary'}`}
                    data-testid={`tasks-sort-${k}`}
                  >
                    {label}
                  </button>
                ))}
              </div>
              {sortBy !== 'attention' && (
                <button
                  type="button"
                  onClick={() => setSortDir(sortDir === 'desc' ? 'asc' : 'desc')}
                  aria-label={sortDir === 'desc' ? 'Newest first. Switch to oldest first' : 'Oldest first. Switch to newest first'}
                  title={sortDir === 'desc' ? 'Newest first' : 'Oldest first'}
                  className="inline-flex items-center gap-0.5 rounded-md border border-soft bg-surface px-1.5 py-1 font-medium text-secondary hover:text-primary"
                  data-testid="tasks-sort-dir"
                >
                  {sortDir === 'desc' ? <ArrowDown className="h-3 w-3" /> : <ArrowUp className="h-3 w-3" />}
                  {sortDir === 'desc' ? 'Newest' : 'Oldest'}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      <TaskQuickAdd onAdd={quickAdd} onMore={(title) => navigate('/tasks/create', { state: title ? { title } : undefined })} autoFocus={empty} />

      {empty && (
        <div className="rounded-xl border border-soft bg-surface p-6 text-center" data-testid="tasks-empty">
          <p className="text-app-body font-semibold text-primary">Nothing here yet</p>
          <p className="mx-auto mt-1 max-w-md text-app-meta text-secondary">
            Put the first thing on your mind in the box above — a title is enough. {personaName} can help you plan it from there, or turn a bigger goal into tasks.
          </p>
          <div className="mt-4 flex flex-wrap justify-center gap-2">
            <Button size="xs" variant="outline" onClick={() => navigate('/horton-ai?from=tasks&type=all')}><Sparkles className="mr-1 h-4 w-4" />Build with {personaName}</Button>
          </div>
        </div>
      )}

      {nothingMatches && (
        <p className="text-app-meta text-muted" data-testid="tasks-no-match">
          Nothing matches “{query}”. <button type="button" onClick={() => setQuery('')} className="text-brand hover:underline">Clear the search</button>
        </p>
      )}

      {!empty && !nothingMatches && groups.map((g) => {
        const isCollapsed = collapsed[g.key] ?? Boolean(g.collapsedByDefault)
        const isDoneGroup = g.key === 'done'
        const items = isDoneGroup && !showAllDone && !query ? g.items.slice(0, DONE_PREVIEW) : g.items
        const Chevron = isCollapsed ? ChevronRight : ChevronDown
        return (
          <section key={g.key} data-testid={`tasks-section-${g.key}`} aria-labelledby={`tasks-h-${g.key}`}>
            <button
              type="button"
              onClick={() => toggle(g.key, Boolean(g.collapsedByDefault))}
              aria-expanded={!isCollapsed}
              className="flex w-full items-center gap-1.5 rounded py-1 text-left hover:text-brand"
              data-testid="tasks-section-toggle"
            >
              <Chevron className="h-3.5 w-3.5 shrink-0 text-muted" aria-hidden="true" />
              <h2 id={`tasks-h-${g.key}`} className="text-app-meta font-bold uppercase tracking-widest text-secondary">{g.title}</h2>
              <span className="text-app-micro text-muted" data-testid="tasks-section-count">{g.items.length}</span>
            </button>
            {!isCollapsed && items.length > 0 && (
              <div className="mt-1 space-y-1.5">
                {items.map((t) => (
                  <TaskRow
                    key={t.id}
                    task={t}
                    today={loadedAt}
                    showParent={groupBy !== 'goal'}
                    showArea={groupBy === 'goal'}
                    personaName={personaName}
                    diaryMode={diaryMode}
                    onDone={complete}
                    onReopen={reopen}
                    onDelete={setDeleteTarget}
                    onChanged={load}
                    timeHint={sortBy === 'attention' ? null : sortBy}
                  />
                ))}
              </div>
            )}
            {!isCollapsed && items.length === 0 && g.quiet && (
              <p className="mt-0.5 pl-5 text-app-meta text-muted" data-testid="tasks-section-quiet">{g.quiet}</p>
            )}
            {!isCollapsed && isDoneGroup && !query && g.items.length > DONE_PREVIEW && (
              <button type="button" onClick={() => setShowAllDone((v) => !v)} className="mt-1.5 pl-5 text-app-meta font-medium text-brand hover:underline" data-testid="tasks-done-more">
                {showAllDone ? 'Show fewer' : `Show all ${g.items.length}`}
              </button>
            )}
          </section>
        )
      })}

      <ConfirmationModal
        isOpen={Boolean(deleteTarget)}
        onClose={() => setDeleteTarget(null)}
        onConfirm={confirmDelete}
        title={actionItemIdOf(deleteTarget || ({} as Task)) ? 'Delete this step?' : 'Delete this task?'}
        message={`“${deleteTarget?.title}” will be removed, along with anything scheduled or set to remind you about it.`}
        variant="error"
        confirmText="Delete"
      />
    </div>
  )
}

export default Tasks
